// File: frontend/src/components/seo-analytics/AhrefsTrafficTab.jsx
// Version: 1.2
// Date: 2026-04-28
// Description: Organic Traffic tab — recharts LineChart of organic_traffic +
//              organic_keywords with 7d / 30d / 90d / 1y range selector.
//              Plus a small KPI strip (white card, 3 columns) showing latest
//              traffic value and a top-3 ratio bar.
// History:
// - [v1.2]: Added "1y" (365 day) range pill to surface backfilled monthly
//           snapshots populated by Phase 07-04 backfill_history.
// - [v1.1]: Light editorial restyle — white KPI strip + chart card,
//           pill-style range selector.
// - [v1.0]: Initial dark theme implementation.

import React, { useState, useEffect, useCallback } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import seoAnalyticsService from '../../services/seoAnalyticsService';

const formatShortDate = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return `${d.getMonth() + 1}/${d.getDate()}`;
  } catch {
    return iso;
  }
};

const formatFull = (n) => (n == null ? '—' : Number(n).toLocaleString());
const formatMoney = (n) =>
  n == null ? '—' : `$${Number(n).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

const AhrefsTrafficTab = ({ domainId }) => {
  const [days, setDays] = useState(30);
  const [trends, setTrends] = useState(null);
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchAll = useCallback(
    async (rangeDays) => {
      if (!domainId) return;
      setLoading(true);
      setError(null);
      try {
        const [tr, ov] = await Promise.all([
          seoAnalyticsService.getAhrefsTrends(domainId, rangeDays),
          seoAnalyticsService.getAhrefsOverview(domainId).catch(() => null),
        ]);
        setTrends(tr || null);
        setOverview(ov || null);
      } catch (err) {
        console.error('[AhrefsTrafficTab] fetch failed:', err);
        setError(err.message || 'Failed to load organic traffic trend.');
      } finally {
        setLoading(false);
      }
    },
    [domainId]
  );

  useEffect(() => {
    fetchAll(days);
  }, [fetchAll, days]);

  const points = (trends?.data_points || [])
    .slice()
    .sort((a, b) => new Date(a.snapshot_date) - new Date(b.snapshot_date));

  const chartData = points.map((p) => ({
    date: formatShortDate(p.snapshot_date),
    organic_traffic: p.organic_traffic,
    organic_keywords: p.organic_keywords,
  }));

  // Top-3 ratio (latest snapshot)
  const totalKw = overview?.organic_keywords || 0;
  const top3 = overview?.top3_count || 0;
  const top3Pct = totalKw > 0 ? Math.min(100, (top3 / totalKw) * 100) : 0;

  const rangePillStyle = (isActive) => ({
    padding: '0.375rem 0.75rem',
    borderRadius: '0.375rem',
    fontSize: '0.875rem',
    fontWeight: 600,
    cursor: loading ? 'not-allowed' : 'pointer',
    border: 'none',
    backgroundColor: isActive ? '#ffffff' : 'transparent',
    color: isActive ? '#0f172a' : '#64748b',
    boxShadow: isActive ? '0 1px 2px rgba(0,0,0,0.05)' : 'none',
    opacity: loading && !isActive ? 0.6 : 1,
    transition: 'background 0.15s, color 0.15s',
  });

  const stripCellStyle = {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.25rem',
  };

  const stripLabelStyle = {
    fontSize: '0.7rem',
    fontWeight: 700,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    color: '#64748b',
  };

  const stripValueStyle = {
    fontSize: '1.25rem',
    fontWeight: 700,
    color: '#0f172a',
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.125rem' }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '0.75rem',
        }}
      >
        <h3 style={{ fontSize: '1.05rem', fontWeight: 700, color: '#0f172a', margin: 0 }}>
          Organic Traffic &amp; Keywords
        </h3>
        <div
          role="group"
          aria-label="Date range"
          style={{
            display: 'inline-flex',
            padding: '4px',
            backgroundColor: '#f1f5f9',
            borderRadius: '0.5rem',
            gap: '2px',
          }}
        >
          {[
            { value: 7, label: '7d' },
            { value: 30, label: '30d' },
            { value: 90, label: '90d' },
            { value: 365, label: '1y' },
          ].map(({ value, label }) => (
            <button
              key={value}
              type="button"
              onClick={() => setDays(value)}
              disabled={loading}
              aria-pressed={days === value}
              aria-label={`Show last ${value} days`}
              style={rangePillStyle(days === value)}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="chart-loading">Loading organic traffic…</div>
      ) : error ? (
        <div className="inline-error">{error}</div>
      ) : !chartData.length ? (
        <div className="empty-state">
          Daily snapshots in progress. First trend visible after 2+ days of data.
        </div>
      ) : (
        <>
          {overview && (
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: '1.25rem',
                backgroundColor: '#ffffff',
                border: '1px solid #e5e7eb',
                borderRadius: '0.75rem',
                padding: '1.25rem 1.5rem',
                boxShadow: '0 1px 3px 0 rgba(0,0,0,0.05)',
              }}
            >
              <div style={stripCellStyle}>
                <span style={stripLabelStyle}>Estimated Value</span>
                <span style={stripValueStyle}>
                  {formatMoney(overview.traffic_value_usd)}/mo
                </span>
              </div>
              <div style={stripCellStyle}>
                <span style={stripLabelStyle}>Latest Traffic</span>
                <span style={stripValueStyle}>
                  {formatFull(overview.organic_traffic)}
                </span>
              </div>
              <div style={{ ...stripCellStyle, gap: '0.5rem' }}>
                <span style={stripLabelStyle}>
                  Top 3 Positions: {formatFull(top3)} of {formatFull(totalKw)} (
                  {top3Pct.toFixed(1)}%)
                </span>
                <div
                  className="ratio-bar"
                  role="progressbar"
                  aria-valuenow={top3Pct.toFixed(0)}
                  aria-valuemin="0"
                  aria-valuemax="100"
                  aria-label={`${top3Pct.toFixed(1)} percent of keywords ranking in top 3`}
                >
                  <div className="ratio-bar-fill" style={{ width: `${top3Pct}%` }} />
                </div>
              </div>
            </div>
          )}

          <div
            style={{
              backgroundColor: '#ffffff',
              border: '1px solid #e5e7eb',
              borderRadius: '0.75rem',
              padding: '1rem',
              boxShadow: '0 1px 3px 0 rgba(0,0,0,0.05)',
            }}
          >
            <ResponsiveContainer width="100%" height={320}>
              <LineChart
                data={chartData}
                margin={{ top: 12, right: 30, left: 12, bottom: 8 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                <YAxis
                  yAxisId="left"
                  tick={{ fontSize: 11 }}
                  tickFormatter={(v) =>
                    Math.abs(v) >= 1000 ? `${(v / 1000).toFixed(1)}k` : v
                  }
                  label={{
                    value: 'Traffic',
                    angle: -90,
                    position: 'insideLeft',
                    fontSize: 11,
                    fill: '#2563eb',
                  }}
                />
                <YAxis
                  yAxisId="right"
                  orientation="right"
                  tick={{ fontSize: 11 }}
                  tickFormatter={(v) =>
                    Math.abs(v) >= 1000 ? `${(v / 1000).toFixed(1)}k` : v
                  }
                  label={{
                    value: 'Keywords',
                    angle: 90,
                    position: 'insideRight',
                    fontSize: 11,
                    fill: '#d97706',
                  }}
                />
                <Tooltip
                  formatter={(val, name) => [
                    formatFull(val),
                    name === 'organic_traffic' ? 'Organic Traffic' : 'Organic Keywords',
                  ]}
                  contentStyle={{ fontSize: 12 }}
                />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Line
                  yAxisId="left"
                  type="monotone"
                  dataKey="organic_traffic"
                  name="Organic Traffic"
                  stroke="#2563eb"
                  strokeWidth={2}
                  dot={{ r: 3 }}
                  activeDot={{ r: 5 }}
                />
                <Line
                  yAxisId="right"
                  type="monotone"
                  dataKey="organic_keywords"
                  name="Organic Keywords"
                  stroke="#d97706"
                  strokeWidth={2}
                  dot={{ r: 3 }}
                  activeDot={{ r: 5 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </>
      )}
    </div>
  );
};

export default AhrefsTrafficTab;
